const REASON_COPY = {
  compatible_base_snapshot_missing: "the existing base intelligence could not be safely reused",
  reuse_not_provable: "unchanged files could not be proven reusable",
  comparison_truncated: "GitHub’s comparison or tree was incomplete",
  legacy_commit_identity_missing: "the existing analysis is not pinned to an exact commit",
  legacy_blob_identity_missing: "file intelligence could not be tied to an exact blob SHA",
  analysis_config_mismatch: "the analysis configuration, model, or processor version does not match",
  analysis_incomplete: "the existing analysis is incomplete or untrusted",
  base_tree_mismatch: "the existing analysis tree does not match this repository",
  base_identity_unavailable: "the live base commit could not be resolved",
  full_head_analysis: "a compatible base analysis was not available for reuse",
  targeted_change_review: "RenderRepo will review the change without reanalyzing the whole repository",
  explicit_full_snapshot: "you chose a verified full snapshot",
  untrusted_legacy_context: "existing whole-repository intelligence is not pinned to exact blob SHAs",
  repository_wide_regression_unverified:
    "RenderRepo can evaluate this change, but cannot claim complete system-wide regression assurance without refreshing the verified base or running a full snapshot",
  targeted_coverage_incomplete: "the GitHub comparison was incomplete, so targeted coverage is not complete",
  targeted_boundary_exhausted: "the targeted safety boundary was reached; more files were not inspected",
};

export function formatSha(sha) {
  if (!sha || typeof sha !== "string") return null;
  return { short: sha.slice(0, 12), full: sha };
}

export function formatPinLabel(pin) {
  const formatted = formatSha(pin?.sha);
  if (!formatted) {
    return pin?.ref
      ? `${pin.ref} · base identity unavailable`
      : "Base identity unavailable";
  }
  return `${pin.ref || "commit"} · ${formatted.short}`;
}

export function humanReuseReason(reason) {
  if (!reason) return REASON_COPY.full_head_analysis;
  return REASON_COPY[reason] || reason.replace(/_/g, " ");
}

export function analysisModeLabel(mode) {
  if (mode === "verified_incremental" || mode === "incremental") {
    return "Verified incremental analysis";
  }
  if (mode === "targeted_change_review") return "Targeted change review";
  if (mode === "verified_full_snapshot" || mode === "full") {
    return "Verified full snapshot";
  }
  return mode || "Analysis";
}

export function isTargetedMode(mode) {
  return mode === "targeted_change_review";
}

export function isVerifiedIncrementalMode(mode) {
  return mode === "verified_incremental" || mode === "incremental";
}

export function isFullSnapshotMode(mode) {
  return mode === "verified_full_snapshot" || mode === "full";
}

export function formatCompareAnalysisCopy(preflight) {
  const changed = preflight?.counts?.changedFiles ?? 0;
  const changedAnalyze =
    preflight?.counts?.changedFilesRequiringAnalysis ??
    (isTargetedMode(preflight?.mode) ? changed : preflight?.counts?.filesToAnalyze ?? 0);
  const related = preflight?.counts?.relatedFilesSelected ?? preflight?.relatedFiles?.length ?? 0;
  const reused = preflight?.counts?.unchangedReusable ?? 0;
  const analyze =
    preflight?.counts?.modelAnalyzedFiles ??
    preflight?.counts?.filesToAnalyze ??
    preflight?.counts?.toAnalyze ??
    0;
  const changedLabel = `${changed} file${changed === 1 ? "" : "s"} changed`;
  if (isVerifiedIncrementalMode(preflight?.mode)) {
    const analyzeLabel =
      analyze === 1 ? "1 file requires analysis" : `${analyze} files require analysis`;
    return `${changedLabel}. ${analyzeLabel}. ${reused} unchanged file${reused === 1 ? "" : "s"} can be reused.`;
  }
  if (isTargetedMode(preflight?.mode)) {
    return `${changedLabel}. ${changedAnalyze} changed file${changedAnalyze === 1 ? "" : "s"} will be analyzed. ${related} related file${related === 1 ? "" : "s"} selected for targeted inspection. ${reused} files safely reused.`;
  }
  return `${changedLabel}. ${analyze} file${analyze === 1 ? "" : "s"} require analysis because ${humanReuseReason(preflight?.reason)}.`;
}

export function primaryConfirmLabel(preflight) {
  if (!preflight?.mode) return "Confirm & Analyze";
  if (isTargetedMode(preflight.mode)) return "Review change";
  if (isVerifiedIncrementalMode(preflight.mode)) return "Confirm incremental analysis";
  return "Confirm full snapshot";
}
